import React, { useEffect, useId, useRef, useState } from "react";
import { CheckCircle2, BookOpen, Sparkles, Hourglass, ArrowRight, Lock } from "lucide-react";

// "Teacher progress" and "Student progress" for the School Progress page (principal and admin): one card per teacher with a
// completion ring, four numbers and a bar per class; then one table per class with every student's activities and stage.
// Everything comes from snapshot.progress_cards. The motion (cards rising in turn, rings drawing with a glow, numbers counting up,
// tiles popping in, bars filling with a light sweep) runs each time the tab is opened, and is off for people who ask their device
// for reduced motion.

// The Super app's own palette (tailwind.config.js): forest greens, sand backgrounds, gold for "watch", warm bronze for "needs help" (no red: it clashes with the greens).
const INK = "#1E3F33";      // forest-700
const TRACK = "#ECE8DE";    // sand

// one colour family per state: a light and a dark end for gradients, a soft tint and the text colour
const TONE = {
    good: { a: "#5B9C86", b: "#2C5E4E", tint: "#E8F1ED", text: "#2C5E4E" },
    mid: { a: "#E0C260", b: "#D4AF37", tint: "#F8F2E1", text: "#8A6D12" },
    low: { a: "#B7A27A", b: "#8C7A4F", tint: "#F3EEE2", text: "#6E5E39" },
    none: { a: "#CFC9BC", b: "#A39E92", tint: "#F2EFEB", text: "#6B675D" },
};
const LEVEL = {
    on_track: { label: "On track", tone: "good" },
    watch: { label: "Watch", tone: "mid" },
    needs_support: { label: "Needs support", tone: "low" },
};
const STAGE = {
    nearly_finished: { label: "Nearly finished", tone: "good" },
    more_than_halfway: { label: "More than halfway", tone: "good" },
    getting_there: { label: "Getting there", tone: "mid" },
    just_beginning: { label: "Just beginning", tone: "low" },
    not_started: { label: "Not started", tone: "none" },
};
const TILE_TONES = {
    done: { a: "#5B9C86", b: "#2C5E4E", tint: "#E8F1ED", text: "#2C5E4E" },
    poems: { a: "#2C5E4E", b: "#1E3F33", tint: "#DCE9E3", text: "#1E3F33" },
    other: { a: "#8FA9A0", b: "#5E7F75", tint: "#EEF2F0", text: "#4A6A60" },
    pending: { a: "#E0C260", b: "#D4AF37", tint: "#F8F2E1", text: "#8A6D12" },
};

const toneOf = (pct) => (pct >= 50 ? TONE.good : pct >= 40 ? TONE.mid : TONE.low);
const fmt = (n) => Number(n || 0).toLocaleString();
const reduced = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const nameOf = (n) => (/\bteacher\b/i.test(n || "") ? n : `Teacher ${n}`);

const CSS = `
@keyframes pcRise { from { opacity: 0; transform: translateY(18px) scale(.985); } to { opacity: 1; transform: none; } }
@keyframes pcPop { 0% { opacity: 0; transform: scale(.82); } 60% { opacity: 1; transform: scale(1.04); } 100% { opacity: 1; transform: none; } }
@keyframes pcSweep { from { transform: translateX(-120%); } to { transform: translateX(320%); } }
@keyframes pcPulse { 0%, 100% { box-shadow: 0 0 0 0 var(--pc-glow); } 50% { box-shadow: 0 0 0 6px transparent; } }
@keyframes pcBreath { 0%, 100% { filter: drop-shadow(0 0 3px var(--pc-glow)); } 50% { filter: drop-shadow(0 0 9px var(--pc-glow)); } }
.pc-rise { animation: pcRise .7s cubic-bezier(.16,1,.3,1) both; }
.pc-pop { animation: pcPop .6s cubic-bezier(.16,1,.3,1) both; }
.pc-ring { transition: stroke-dashoffset 1.5s cubic-bezier(.22,1,.36,1); }
.pc-ring-wrap { animation: pcBreath 3.2s ease-in-out 1.8s infinite; }
.pc-bar { transition: width 1.2s cubic-bezier(.22,1,.36,1); position: relative; overflow: hidden; }
.pc-bar::after { content: ""; position: absolute; inset: 0; width: 38%; background: linear-gradient(90deg, transparent, rgba(255,255,255,.55), transparent); transform: translateX(-120%); animation: pcSweep 1.1s ease-out var(--pc-sweep, 1.2s) 1 both; }
.pc-dot { animation: pcPulse 2.2s ease-in-out infinite; }
.pc-card { transition: transform .3s cubic-bezier(.2,.8,.2,1), box-shadow .3s ease; }
.pc-card:hover { transform: translateY(-3px); box-shadow: 0 14px 34px -14px rgba(30,63,51,.28); }
.pc-tile { transition: transform .25s ease, box-shadow .25s ease; }
.pc-tile:hover { transform: translateY(-2px); box-shadow: 0 8px 18px -10px rgba(30,63,51,.35); }
.pc-row { transition: background-color .2s ease; }
.pc-row:hover { background: #FBFAF6; }
@media (prefers-reduced-motion: reduce) { .pc-rise, .pc-pop, .pc-ring-wrap, .pc-dot { animation: none; } .pc-ring, .pc-bar { transition: none; } .pc-bar::after { display: none; } .pc-card, .pc-tile { transition: none; } }
`;

// true one frame after mount, so the "from" state is painted first and the CSS transitions have something to run from
function useGo() {
    const [go, setGo] = useState(reduced());
    useEffect(() => {
        if (go) return undefined;
        const id = requestAnimationFrame(() => requestAnimationFrame(() => setGo(true)));
        return () => cancelAnimationFrame(id);
    }, [go]);
    return go;
}

function CountUp({ value, decimals = 0, suffix = "", delay = 0, ms = 1500 }) {
    const [shown, setShown] = useState(reduced() ? value : 0);
    const raf = useRef(0);
    useEffect(() => {
        if (reduced()) { setShown(value); return undefined; }
        let t0 = null;
        const tick = (t) => {
            if (t0 === null) t0 = t + delay;
            const p = Math.min(Math.max((t - t0) / ms, 0), 1);
            setShown(value * (1 - Math.pow(1 - p, 4)));
            if (p < 1) raf.current = requestAnimationFrame(tick);
        };
        raf.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf.current);
    }, [value, delay, ms]);
    const n = decimals ? shown.toFixed(decimals) : Math.round(shown).toLocaleString();
    return <>{n}{suffix}</>;
}

function Ring({ pct, tone, go, delay = 0, size = 96, decimals = 1 }) {
    const id = useId().replace(/:/g, "");
    const r = 38;
    const c = 2 * Math.PI * r;
    const off = c * (1 - (go ? Math.min(pct, 100) : 0) / 100);
    return (
        <div className="pc-ring-wrap relative shrink-0" style={{ width: size, height: size, "--pc-glow": `${tone.b}55` }}>
            <svg viewBox="0 0 96 96" width={size} height={size} className="-rotate-90">
                <defs>
                    <linearGradient id={`g${id}`} x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor={tone.a} /><stop offset="100%" stopColor={tone.b} /></linearGradient>
                </defs>
                <circle cx="48" cy="48" r={r} fill="none" stroke={TRACK} strokeWidth="9" />
                <circle cx="48" cy="48" r={r} fill="none" stroke={`url(#g${id})`} strokeWidth="9" strokeLinecap="round"
                    strokeDasharray={c} strokeDashoffset={off} className="pc-ring" style={{ transitionDelay: `${delay}ms` }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
                <span className="font-display font-bold text-[19px]" style={{ color: INK }}><CountUp value={pct} decimals={decimals} suffix="%" delay={delay} /></span>
                <span className="text-[9px] uppercase tracking-wider mt-1" style={{ color: tone.text }}>complete</span>
            </div>
        </div>
    );
}

function Bar({ pct, go, delay = 0, tone, h = 9 }) {
    const t = tone || toneOf(pct);
    return (
        <div className="w-full rounded-full overflow-hidden" style={{ background: TRACK, height: h }}>
            <div className="pc-bar h-full rounded-full"
                style={{ width: go ? `${Math.max(Math.min(pct, 100), pct > 0 ? 1.8 : 0)}%` : "0%", background: `linear-gradient(90deg, ${t.a}, ${t.b})`,
                    transitionDelay: `${delay}ms`, "--pc-sweep": `${delay + 1100}ms` }} />
        </div>
    );
}

function Tile({ value, label, icon: Icon, tone, delay }) {
    return (
        <div className="pc-tile pc-pop rounded-2xl px-3 py-3 min-w-0 flex items-center gap-3" style={{ background: tone.tint, animationDelay: `${delay}ms` }}>
            <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-white" style={{ background: `linear-gradient(135deg, ${tone.a}, ${tone.b})` }}>
                <Icon className="w-[18px] h-[18px]" strokeWidth={2.4} />
            </span>
            <div className="min-w-0">
                <div className="font-display font-bold text-2xl leading-none" style={{ color: tone.text }}><CountUp value={value} delay={delay} /></div>
                <div className="text-[11px] mt-1 text-foreground/70 truncate">{label}</div>
            </div>
        </div>
    );
}

export function TeacherProgressCards({ data }) {
    const go = useGo();
    if (!data) return <div className="rounded-2xl bg-white border border-border p-6 text-sm text-foreground/70">Loading…</div>;
    const teachers = data.teachers || [];
    if (!teachers.length) {
        return <div className="rounded-2xl bg-white border border-border p-6 text-sm text-foreground/70" data-testid="teacher-progress-empty">No teachers have students yet.</div>;
    }
    return (
        <section className="space-y-5" data-testid="teacher-progress">
            <style>{CSS}</style>
            <div>
                <h2 className="font-display text-xl font-bold text-forest-700">Teacher progress</h2>
                <p className="text-sm text-foreground/70 mt-1">Activities completed and pending, overall completion, and how each teacher is doing.</p>
            </div>
            {teachers.map((t, i) => {
                const lv = LEVEL[t.level] || LEVEL.on_track;
                const tone = TONE[lv.tone];
                const d0 = i * 140;
                return (
                    <article key={t.teacher_guardian_id} className="pc-rise pc-card relative overflow-hidden rounded-3xl bg-white border border-border shadow-sm p-5 md:p-6"
                        style={{ animationDelay: `${d0}ms` }} data-testid={`teacher-card-${t.teacher_guardian_id}`}>
                        <span className="absolute inset-x-0 top-0 h-1.5" style={{ background: `linear-gradient(90deg, ${tone.a}, ${tone.b})` }} />
                        <header className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <span className="w-8 h-8 rounded-full text-white text-sm font-bold flex items-center justify-center shrink-0" style={{ background: `linear-gradient(135deg, #2C5E4E, ${INK})` }}>{i + 1}</span>
                                <div className="min-w-0">
                                    <h3 className="font-display font-bold text-lg truncate" style={{ color: INK }}>{nameOf(t.name)}</h3>
                                    <p className="text-xs text-foreground/70">{fmt(t.students)} students · {t.classes.length} class{t.classes.length === 1 ? "" : "es"}</p>
                                </div>
                            </div>
                            <span className="rounded-full pl-2.5 pr-3 py-1 text-xs font-semibold shrink-0 inline-flex items-center gap-1.5" style={{ background: tone.tint, color: tone.text }}>
                                <span className="pc-dot w-2 h-2 rounded-full" style={{ background: tone.b, "--pc-glow": `${tone.b}88` }} />{lv.label}
                            </span>
                        </header>

                        <div className="mt-4 flex flex-col sm:flex-row gap-5 sm:items-center">
                            <Ring pct={t.pct} tone={tone} go={go} delay={d0 + 150} />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm italic text-foreground/70 mb-3">{t.note}</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                                    <Tile value={t.done} label="Activities done" icon={CheckCircle2} tone={TILE_TONES.done} delay={d0 + 300} />
                                    <Tile value={t.poems} label="Poems completed" icon={BookOpen} tone={TILE_TONES.poems} delay={d0 + 380} />
                                    <Tile value={t.other} label="Other activities" icon={Sparkles} tone={TILE_TONES.other} delay={d0 + 460} />
                                    <Tile value={t.pending} label="Pending" icon={Hourglass} tone={TILE_TONES.pending} delay={d0 + 540} />
                                </div>
                            </div>
                        </div>

                        <div className="mt-5 pt-4 border-t border-dashed border-border space-y-3">
                            {t.classes.map((c, j) => (
                                <div key={c.key} className="grid grid-cols-[minmax(90px,150px)_1fr] sm:grid-cols-[minmax(110px,170px)_1fr_auto] items-center gap-x-4 gap-y-1">
                                    <span className="text-sm font-semibold truncate" style={{ color: INK }}>{c.label}</span>
                                    <div className="flex items-center gap-3 min-w-0">
                                        <Bar pct={c.pct} go={go} delay={d0 + 400 + j * 150} />
                                        <span className="text-sm font-bold w-14 text-right shrink-0" style={{ color: toneOf(c.pct).text }}>{c.pct}%</span>
                                    </div>
                                    <span className="col-span-2 sm:col-span-1 text-xs text-foreground/70">
                                        {fmt(c.done)} of {fmt(c.done + c.pending)} activities
                                    </span>
                                </div>
                            ))}
                        </div>
                    </article>
                );
            })}
            <p className="text-xs text-foreground/70">
                An activity is one lesson step of a poem. Each child has {fmt(data.per_child)} ({fmt(data.poems_total)} poems × {data.steps} steps). Activities done = poems completed × {data.steps} + other activities; done + pending = {fmt(data.per_child)}.
                On track 50%+ · Watch 40–49% · Needs support below 40%.
            </p>
        </section>
    );
}

export function StudentProgressTables({ data }) {
    const go = useGo();
    const [closed, setClosed] = useState({});
    if (!data) return <div className="rounded-2xl bg-white border border-border p-6 text-sm text-foreground/70">Loading…</div>;
    const classes = (data.classes || []).filter((c) => c.students > 0);
    if (!classes.length) {
        return <div className="rounded-2xl bg-white border border-border p-6 text-sm text-foreground/70" data-testid="student-progress-empty">No students yet.</div>;
    }
    return (
        <section className="space-y-5" data-testid="student-progress">
            <style>{CSS}</style>
            <div>
                <h2 className="font-display text-xl font-bold text-forest-700">Student progress</h2>
                <p className="text-sm text-foreground/70 mt-1">Completed and pending activities, overall progress and the stage each child has reached.</p>
            </div>
            {classes.map((c, i) => {
                const d0 = Math.min(i, 6) * 110;
                const collapsed = !!closed[c.key];
                const ct = toneOf(c.pct);
                return (
                    <article key={c.key} className="pc-rise pc-card rounded-2xl bg-white border border-border shadow-sm overflow-hidden"
                        style={{ animationDelay: `${d0}ms` }} data-testid={`student-class-${c.key}`}>
                        <button type="button" onClick={() => setClosed((m) => ({ ...m, [c.key]: !m[c.key] }))} aria-expanded={!collapsed}
                            className="w-full flex items-center justify-between gap-3 flex-wrap px-5 py-3.5 text-left"
                            style={{ background: "linear-gradient(90deg, #F6F3EA, #FFFFFF)", borderLeft: `5px solid ${ct.b}` }}>
                            <span>
                                <span className="font-display font-bold text-base" style={{ color: INK }}>{c.label}</span>
                                <span className="text-xs text-foreground/70 ml-2">{nameOf(c.teacher_name)}</span>
                            </span>
                            <span className="text-xs text-foreground/70">
                                {c.students} students · {fmt(c.done)} of {fmt(c.done + c.pending)} activities{" "}
                                <span className="font-display font-bold text-base ml-1" style={{ color: ct.text }}>{c.pct}%</span>
                            </span>
                        </button>
                        {!collapsed && (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm min-w-[720px]" style={{ tableLayout: "fixed" }}>
                                    <colgroup><col style={{ width: 44 }} /><col /><col style={{ width: 64 }} /><col style={{ width: 64 }} /><col style={{ width: 64 }} /><col style={{ width: 72 }} /><col style={{ width: 190 }} /><col style={{ width: 64 }} /><col style={{ width: 170 }} /></colgroup>
                                    <thead>
                                        <tr className="text-[10px] uppercase tracking-wider text-foreground/70 border-b border-border" style={{ background: "#FBFAF6" }}>
                                            <th className="text-left font-semibold pl-5 py-2">#</th>
                                            <th className="text-left font-semibold py-2">Student</th>
                                            <th className="text-right font-semibold py-2 px-2">Done</th>
                                            <th className="text-right font-semibold py-2 px-2">Poems</th>
                                            <th className="text-right font-semibold py-2 px-2">Other</th>
                                            <th className="text-right font-semibold py-2 px-2">Pending</th>
                                            <th className="text-left font-semibold py-2 px-3">Progress</th>
                                            <th className="text-right font-semibold py-2 px-2">%</th>
                                            <th className="text-left font-semibold py-2 pl-3 pr-5">Stage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {c.rows.map((s, j) => {
                                            const st = STAGE[s.stage] || STAGE.not_started;
                                            const stt = TONE[st.tone];
                                            return (
                                                <tr key={`${s.name}-${j}`} className="pc-rise pc-row border-b border-border/60 last:border-0" style={{ animationDelay: `${d0 + 150 + Math.min(j, 12) * 35}ms` }}>
                                                    <td className="pl-5 py-1.5 text-xs text-foreground/70">{j + 1}</td>
                                                    <td className="py-1.5 font-semibold truncate" style={{ color: INK }}>{s.name}</td>
                                                    <td className="py-1.5 px-2 text-right font-bold" style={{ color: TILE_TONES.done.text }}>{fmt(s.done)}</td>
                                                    <td className="py-1.5 px-2 text-right">{fmt(s.poems)}</td>
                                                    <td className="py-1.5 px-2 text-right">{fmt(s.other)}</td>
                                                    <td className="py-1.5 px-2 text-right font-bold" style={{ color: TILE_TONES.pending.text }}>{fmt(s.pending)}</td>
                                                    <td className="py-1.5 px-3"><Bar pct={s.pct} go={go} delay={d0 + 300 + Math.min(j, 12) * 45} h={8} /></td>
                                                    <td className="py-1.5 px-2 text-right font-bold" style={{ color: toneOf(s.pct).text }}>{s.pct}%</td>
                                                    <td className="py-1.5 pl-3 pr-5">
                                                        <span className="rounded-full pl-2 pr-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap inline-flex items-center gap-1.5" style={{ background: stt.tint, color: stt.text }}>
                                                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: stt.b }} />{st.label}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </article>
                );
            })}
        </section>
    );
}

// "Learning progress" on a learner's own Overview, in the same look as the teacher cards: one card per app with a coloured top edge,
// a completion ring, the app's summary line, a status pill and (for a family) a bar per learner. `status` says whether the numbers are
// available; `statusText` is the line shown when they are not; a card with numbers opens its details through `onOpen`.
export function LearningProgressCards({ items, statusText, lockedText, onOpen }) {
    const go = useGo();
    return (
        <div className="grid sm:grid-cols-2 gap-4">
            <style>{CSS}</style>
            {items.map((p, i) => {
                const ok = p.status === "ok";
                const pct = ok ? Number(p.percent) || 0 : 0;
                const tone = !ok ? TONE.none : toneOf(pct);   // the teacher cards' colours: green, gold, bronze
                const label = !ok ? "" : pct >= 50 ? "On track" : pct >= 40 ? "Watch" : pct > 0 ? "Getting started" : "Not started";
                const d0 = Math.min(i, 8) * 90;
                const click = ok ? { role: "button", tabIndex: 0, onClick: () => onOpen(p), "aria-haspopup": "dialog",
                    onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(p); } } } : {};
                return (
                    <article key={p.product} {...click} data-testid={`progress-${p.product}`}
                        className={`pc-rise pc-card relative overflow-hidden rounded-3xl bg-white border border-border shadow-sm p-5 ${ok ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-600/40" : ""}`}
                        style={{ animationDelay: `${d0}ms` }}>
                        <span className="absolute inset-x-0 top-0 h-1.5" style={{ background: `linear-gradient(90deg, ${tone.a}, ${tone.b})` }} />
                        <header className="flex items-start justify-between gap-3">
                            <h3 className="font-display font-bold text-lg" style={{ color: INK }}>{p.name}</h3>
                            {ok && <span className="rounded-full pl-2.5 pr-3 py-1 text-xs font-semibold shrink-0 inline-flex items-center gap-1.5" style={{ background: tone.tint, color: tone.text }}>
                                <span className="w-2 h-2 rounded-full" style={{ background: tone.b }} />{label}</span>}
                        </header>
                        {ok ? (<>
                            <div className="mt-4 flex items-center gap-4">
                                <Ring pct={pct} tone={tone} go={go} delay={d0 + 150} size={84} decimals={0} />
                                <p className="text-sm text-foreground/75 min-w-0">{p.summary}</p>
                            </div>
                            {p.learners?.length > 0 && (
                                <div className="mt-4 pt-3 border-t border-dashed border-border space-y-2">
                                    {p.learners.map((l, j) => (
                                        <div key={l.name} className="grid grid-cols-[minmax(80px,120px)_1fr_auto] items-center gap-3">
                                            <span className="text-sm font-semibold truncate" style={{ color: INK }}>{l.name}</span>
                                            <Bar pct={l.percent} go={go} delay={d0 + 300 + j * 120} h={8} />
                                            <span className="text-sm font-bold w-12 text-right" style={{ color: toneOf(l.percent).text }}>{l.percent}%</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="mt-4 text-xs font-semibold inline-flex items-center gap-1" style={{ color: INK }}>View details <ArrowRight className="h-3.5 w-3.5" /></div>
                        </>) : (
                            <p className="mt-3 text-sm text-foreground/65 inline-flex items-center gap-2">
                                {p.status === "locked" && <Lock className="h-4 w-4 shrink-0" />}{p.status === "locked" && lockedText ? lockedText : statusText[p.status] || p.status}
                            </p>
                        )}
                    </article>
                );
            })}
        </div>
    );
}

export { Ring as ProgressRing, Bar as GradientBar, useGo as useProgressGo, CSS as PROGRESS_CSS, TONE as PROGRESS_TONE, TILE_TONES, INK as PROGRESS_INK, toneOf as progressTone, CountUp };
